import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, Download, FileBarChart, CheckCircle2, ArrowRight, Clock } from "lucide-react";
import { api } from "../lib/api";
import { cmaService } from "../services/cmaService";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { track } from "../lib/analytics";
import { Eyebrow, StatusPill } from "../components/Bits";

const aed = (n) => "AED " + Number(n).toLocaleString();

export default function BrokerDashboard() {
  const { user, ready } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [reports, setReports] = useState([]);
  const [pilot, setPilot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (ready && !user) nav("/auth"); }, [ready, user, nav]);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      api.get("/broker/cma/reports").then((r) => setReports(r.data)).catch(() => setReports([])),
      api.get("/broker/pilot/me").then((r) => setPilot(r.data)).catch(() => setPilot({ status: "none" })),
    ]).finally(() => setLoading(false));
  }, [user]);

  if (!ready || !user || loading)
    return <div className="p-16 text-white/50 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> {t("common.loading")}</div>;

  const applied = pilot && pilot.status && pilot.status !== "none";
  const approved = pilot?.status === "approved";

  return (
    <div className="max-w-[1200px] mx-auto px-5 lg:px-8 py-14" data-testid="broker-dashboard-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Eyebrow>{t("broker.eyebrow")}</Eyebrow>
          <h1 className="font-display text-4xl text-white mt-1">{t("broker.dash_title")}</h1>
          <p className="text-white/55 mt-2">{t("broker.dash_sub")}</p>
        </div>
        <Link to="/partners/broker" data-testid="broker-open-workbench" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gold text-ink font-semibold hover:brightness-110 transition-all duration-200">
          {t("broker.dash_open")} <ArrowRight size={15} />
        </Link>
      </div>

      {/* Pilot status */}
      <div className="glass rounded-2xl p-6 mt-8" data-testid="broker-pilot-status">
        <div className="text-xs uppercase tracking-[0.2em] text-gold/70 mb-3">{t("broker.dash_pilot_h")}</div>
        {applied ? (
          <div className="flex items-start gap-3">
            <CheckCircle2 size={22} className={approved ? "text-emerald-400 mt-0.5" : "text-gold mt-0.5"} />
            <div>
              <div className="text-white font-medium">{approved ? t("broker.pilot_approved") : t("broker.dash_pilot_applied")}</div>
              <div className="text-white/55 text-sm mt-0.5">{approved ? t("broker.dash_pilot_applied_sub") : t("broker.dash_pilot_applied_sub")}</div>
              <div className="text-white/40 text-xs mt-1 flex items-center gap-1.5"><Clock size={12} /> {pilot.agency} · {new Date(pilot.created_at).toLocaleDateString()}</div>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="text-white/60 text-sm">{t("broker.dash_pilot_none")}</div>
            <Link to="/partners/broker" data-testid="broker-apply-pilot" className="text-sm text-gold border border-gold/25 rounded-lg px-4 py-2 hover:bg-gold/10 transition-colors duration-200">{t("broker.dash_pilot_cta")}</Link>
          </div>
        )}
      </div>

      {/* Reports */}
      <div className="mt-8">
        <h2 className="font-display text-2xl text-white mb-4">{t("broker.dash_reports_h")}</h2>
        {reports.length === 0 ? (
          <div className="glass rounded-2xl p-8 text-center" data-testid="broker-reports-empty">
            <FileBarChart size={28} className="text-gold/70 mx-auto" />
            <p className="text-white/60 mt-3">{t("broker.dash_empty")}</p>
            <Link to="/partners/broker" className="inline-flex items-center gap-1.5 mt-4 text-sm text-gold">{t("broker.dash_run_cta")} <ArrowRight size={14} /></Link>
          </div>
        ) : (
          <div className="glass rounded-2xl overflow-hidden" data-testid="broker-reports-list">
            <div className="hidden md:grid grid-cols-12 px-5 py-3 text-[11px] uppercase tracking-wider text-white/40 border-b border-white/10">
              <div className="col-span-4">{t("broker.col_subject")}</div>
              <div className="col-span-2">{t("broker.col_confidence")}</div>
              <div className="col-span-2">{t("broker.col_deviation")}</div>
              <div className="col-span-3">{t("broker.col_range")}</div>
              <div className="col-span-1 text-right">{t("common.export")}</div>
            </div>
            {reports.map((r) => (
              <div key={r.report_id} data-testid="broker-report-row" className="grid grid-cols-2 md:grid-cols-12 gap-2 items-center px-5 py-4 border-b border-white/5 hover:bg-white/[0.02] transition-colors duration-200">
                <div className="md:col-span-4">
                  <div className="text-white text-sm">{r.building || r.area} · {r.bedrooms}BR</div>
                  <div className="text-white/40 text-xs">{r.area} · {r.size_sqft} sqft · {aed(r.asking_price)}</div>
                </div>
                <div className="md:col-span-2">
                  {r.insufficient ? <StatusPill status="coming" /> : <span className="text-sm text-gold">{r.confidence}</span>}
                </div>
                <div className="md:col-span-2 text-sm text-white/70">{r.insufficient ? "—" : `${r.deviation_pct > 0 ? "+" : ""}${r.deviation_pct}%`}</div>
                <div className="md:col-span-3 text-sm text-white/70">{r.insufficient ? "—" : `${aed(r.suggested_price_low)} – ${aed(r.suggested_price_high)}`}</div>
                <div className="md:col-span-1 flex items-center justify-end gap-1.5">
                  <button onClick={() => nav(`/partners/broker?report=${r.report_id}`)} data-testid="broker-report-reopen"
                    className="text-xs text-white/70 border border-white/10 rounded-lg px-2.5 py-1.5 hover:border-gold/40 hover:text-gold transition-colors duration-200">{t("broker.reopen")}</button>
                  <button onClick={() => { track("cma_export", { report_id: r.report_id }); window.open(cmaService.exportUrl(r.report_id), "_blank"); }}
                    data-testid="broker-report-export" className="inline-flex items-center gap-1 text-xs text-gold border border-gold/20 rounded-lg px-2.5 py-1.5 hover:bg-gold/10 transition-colors duration-200">
                    <Download size={13} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
